import * as Constants from "_core/configs/Constants";
import * as SceneConfig from "_core/configs/SceneConfig";
import dataManagerInstance from "_core/managers/DataManager";
import { getTime, waitSecond } from "_core/utils/common";

// タイムアウト判定用(fetchのabortと同じ扱いにするためXHR側でも投げる)
class TimeoutError extends Error {
    constructor() {
        super('timeout');
        this.name = 'TimeoutError';
    }
}

/**
 * 汎用のAPIリクエスト実行
 * @param {string} url 
 * @param {string} method 
 * @param {array} requestParams 
 * @returns 
 */
export const ApiRequest = async ({ url, method = 'POST', requestParams = {}, timeout = 60000 ,requestBinaryParams = {}, auth = true, isExternalApi = false, onUploadProgress = null} ) => {

    // リクエストパラメータを取得(設定されたパラメータ内容によって変動)
    const GetRequestParams = (requestParams, requestBinaryParam) => {
        if(Object.keys(requestBinaryParam).length >= 1)
        {
            // リクエストパラメータを準備
            const params = new FormData();
            for (const key in requestParams) {
                if (requestParams.hasOwnProperty(key)) {
                    params.append(key, requestParams[key]);
                }
            }
            for ( const key in requestBinaryParam)
            {
                if (requestBinaryParam.hasOwnProperty(key)) {
                    params.append(key, requestBinaryParam[key]['file'], requestBinaryParam[key]['filename']);
                }
            }

            return params;
        }

        // リクエストパラメータを準備
        const params = new URLSearchParams();
        for (const key in requestParams) {
            if (requestParams.hasOwnProperty(key)) {
                const value = requestParams[key];

                if (Array.isArray(value)) {
                    value.forEach(v => {
                        params.append(key, v);
                    });
                } else {
                    params.append(key, value);
                }
            }
        }
        return params;
    }

    const isBinaryRequestParam = Object.keys(requestBinaryParams).length >= 1;

    // XHRでのリクエスト実行
    // fetchは送信進捗を取得できないため、ファイル送信時のみこちらを使う
    const RequestByXhr = (requestUrl, headers, body) => {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open(method, requestUrl, true);
            xhr.withCredentials = true;
            xhr.timeout = timeout;

            for (const key in headers) {
                if (headers.hasOwnProperty(key)) {
                    xhr.setRequestHeader(key, headers[key]);
                }
            }

            if (onUploadProgress)
            {
                xhr.upload.onprogress = (event) => {
                    // 送信総量が不明な場合は進捗を算出できない
                    if (! event.lengthComputable) {
                        return;
                    }
                    onUploadProgress(event.loaded / event.total);
                };
            }

            xhr.onload = () => {
                if (xhr.status < 200 || xhr.status >= 300) {
                    reject(new Error('Network response was not ok'));
                    return;
                }

                try {
                    resolve(JSON.parse(xhr.responseText));
                } catch (e) {
                    reject(e);
                }
            };
            xhr.onerror = () => { reject(new Error('Network response was not ok')) };
            xhr.onabort = () => { reject(new Error('Network response was not ok')) };
            xhr.ontimeout = () => { reject(new TimeoutError()) };

            xhr.send(body);
        });
    };

    // リクエストパラメータを準備
    // const params = new URLSearchParams();
    // for (const key in requestParams) {
    //     if (requestParams.hasOwnProperty(key)) {
    //         params.append(key, requestParams[key]);
    //     }
    // }
    const params = GetRequestParams(requestParams, requestBinaryParams);

    // 多重実行チェック
    let nowTime = getTime()

    const oldApiTimestamp = dataManagerInstance.getApiTimestamp()
    // APIタイムスタンプが前回送信時と同じであれば、1秒待機後に送信する
    if(nowTime == oldApiTimestamp)
        {
        await waitSecond(1);
        nowTime = getTime()
    }

    if(auth)
    {
        params.append("api_timestamp", nowTime);
        params.append("is_editor", true);
    }

    const scene = url.split('/')[1];
    const isAdminScene = SceneConfig.ADMIN_CHECK_SCENE_LIST.includes(scene);

    const tUser = dataManagerInstance.getTUser();
    const tAccount = dataManagerInstance.getTAccount();

    // タイムアウト設定(XHR側は xhr.timeout で設定する)
    const controller = new AbortController();
    if (! isBinaryRequestParam)
    {
        setTimeout(() => { controller.abort() }, timeout);
    }

    try {
        let headers = {
            'accept': 'application/json',
            // APIサーバーとドメインが異なる場合は必要
            // 'Access-Control-Allow-Origin': 'http://localhost:3000'
        };
        if(!isBinaryRequestParam)
        {
            headers['Content-Type'] = 'application/x-www-form-urlencoded';
        }

        // console.log('ApiRequest 開始 headers = '+ headers.toString());
        // APIリクエスト開始
        console.log('ApiRequest 開始 url = ' + Constants.BASE_URL + url);
        let option = {
            mode: 'cors',
            method: method,
            credentials: "include",
            headers: headers,
            signal: controller.signal,
        };

        if (method === 'POST')
        {
            option['body'] =  isBinaryRequestParam ? params : params.toString();
        }

        let requestUrl = Constants.BASE_URL + url
        console.log(isExternalApi);
        if (isExternalApi)
        {
            console.log("URLを外部形式に書き換え");
            requestUrl = url
        }
        console.log(requestUrl);

        // ファイル送信時は送信進捗を取得するためXHRで実行する
        if (isBinaryRequestParam)
        {
            const result = await RequestByXhr(requestUrl, headers, option['body']);
            console.log('ApiRequest 終了');
            return result;
        }

        const response = await fetch(requestUrl, option);
        console.log('ApiRequest 終了');

        // レスポンスがOK出ない場合はエラーを返す
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        const result = await response.json();
        return result;
    } catch (error) {
        // タイムアウト(fetchはabort、XHRはtimeout)は原因が分かるよう文言を分ける
        const isTimeout = (error && (error.name === 'TimeoutError' || error.name === 'AbortError'));

        const result = {
            error_code: 404,
            error_detail: {
                "code": 900000,
                "title": "エラー",
                "message": isTimeout ? Constants.TIMEOUT_ERROR_DETAIL : Constants.UNKNOWN_ERROR_DETAIL,
                "event": 0,
                "value": ""
            },
            now_time: nowTime
        }
        return result;
    }
};
